'use client';

import { useStoreMap, useUnit } from 'effector-react';
import { FileInfoPopup } from '@nx-frontend/features/file-manager';
import { ManageMembers } from '@nx-frontend/features/manage-members';
import { $ModalStore } from '@nx-frontend/shared/ui/modal/modal-store';
import {
  AddPageToPortalPayload,
  CustomizerUnpublishedChangesPopupPayload,
  DialogPopupPayload,
  EditPortalLinkPayload,
  EditPortalPagePayload,
  FileInfoPayload,
  ManageMembersPayload,
  PageSettingInPortalPayload,
  PopupType,
  TranscriptADPopupPayload,
} from '@nx-frontend/shared/types';
import { AddPageToPortalPopup } from './add-page-to-portal';
import { AddonPopup } from './addon-popup';
import { AutomationLimitLTDPopup } from './automation-limit-ltd-popup';
import { CreatePortalEntityPopup } from './create-portal-entity';
import { CreatePortalLinkPopup } from './create-portal-link-popup';
import { CustomizerUnpublishedChangesPopup } from './customizer-unpublished-changes-popup';
import { DeleteProjectionOriginPopup } from './delete-projection-origin-popup';
import { DialogPopup } from './dialog-popup';
import { EditPortalLinkPopup } from './edit-portal-link';
import { EditPortalPagePopup } from './edit-portal-page';
import { EmojiPopup } from './emoji-popup';
import { FromCallPopup } from './from-call';
import { FromCallEndPopup } from './from-call-end';
import { GetAutomationPopup } from './get-automation-popup';
import { GetTranscribePopup } from './get-transcribe-popup';
import { InvitePopup } from './invite-popup';
import { ManagePopup } from './manage-popup';
import { TemplatePagePopup } from './new-template-page-popup';
import { PageSettingsInPortalPopup } from './page-settings-in-portal-popup';
import { PaywallPopup } from './paywall-popup';
import { PortalIsReadyPopup } from './portal-is-ready-popup';
import { ProcessViewIntroPopup } from './process-view-intro-popup';
import { PublishInformPopup } from './publish-inform-popup';
import { PurchaseAutomationBillingPopup } from './purchase-automation-billing-popup';
import { PurchaseTranscribeBillingPopup } from './purchase-transcribe-billing-popup';
import { RenameNodePopup } from './rename-node-popup';
import { SharePopup } from './share-popup';
import { SuccessMoveCopyPopup } from './success-move-copy-popup';
import { TextFromImagePopup } from './text-from-image-popup';
import { TranscriptADPopup } from './transcript-ad-popup';
import { TranscriptLimitLTDPopup } from './transcript-limit-ltd-popup';
import { TrialWelcomePopup } from './trial-welcome-popup';
import { UnlockAIBillingPopup } from './unlock-ai-billing-popup';
import { UnlockAIPopup } from './unlock-ai-popup';
import { WizardExitPopup } from './wizard-exit-popup';
import { WizardExitThanksPopup } from './wizard-exit-thanks';

export function Popups({ modalId }: { modalId?: number }) {
  const [modalStore] = useUnit([$ModalStore]);

  const modal = useStoreMap({
    keys: [modalId],
    store: $ModalStore,
    fn: (state, [modalId]) => {
      return state.find((content) => content.id === modalId);
    },
  });
  const modalContent = modal?.content;

  if (modalContent === PopupType.INVITE) {
    return <InvitePopup />;
  } else if (modalContent === PopupType.RENAME) {
    return <RenameNodePopup />;
  } else if (modalContent === PopupType.TEMPLATE_PAGE) {
    return <TemplatePagePopup />;
  } else if (modalContent === PopupType.MANAGE) {
    return <ManagePopup />;
  } else if (modalContent === PopupType.EMOJI) {
    return <EmojiPopup />;
  } else if (modalContent === PopupType.TEXT_FROM_IMAGE) {
    return <TextFromImagePopup />;
  } else if (modalContent === PopupType.PAYWALL) {
    return <PaywallPopup />;
  } else if (modalContent === PopupType.UNLOCK_AI) {
    return <UnlockAIPopup />;
  } else if (modalContent === PopupType.UNLOCK_AI_BILLING) {
    return <UnlockAIBillingPopup />;
  } else if (modalContent === PopupType.SHARE) {
    return <SharePopup />;
  } else if (modalContent === PopupType.TRIAL_WELCOME) {
    return <TrialWelcomePopup />;
  } else if (modalContent === PopupType.FROM_CALL) {
    return <FromCallPopup />;
  } else if (modalContent === PopupType.FROM_CALL_END) {
    return <FromCallEndPopup />;
  } else if (modalContent === PopupType.PAGE_SETTING_IN_PORTAL) {
    const modal = modalStore.find((m) => m.content === PopupType.PAGE_SETTING_IN_PORTAL);
    if (!modal || !modal.payload) {
      return <div></div>;
    }
    const payload = modal.payload as PageSettingInPortalPayload;
    return <PageSettingsInPortalPopup content={payload} />;
  } else if (modalContent === PopupType.ADD_PAGE_TO_PORTAL) {
    const modal = modalStore.find((m) => m.content === PopupType.ADD_PAGE_TO_PORTAL);
    if (!modal || !modal.payload) {
      return <div></div>;
    }
    const payload = modal.payload as AddPageToPortalPayload;
    return <AddPageToPortalPopup content={payload} />;
  } else if (modalContent === PopupType.EDIT_PORTAL_PAGE) {
    const modal = modalStore.find((m) => m.content === PopupType.EDIT_PORTAL_PAGE);
    if (!modal || !modal.payload) {
      return <div></div>;
    }
    const payload = modal.payload as EditPortalPagePayload;
    return <EditPortalPagePopup content={payload} />;
  } else if (modalContent === PopupType.EDIT_PORTAL_LINK) {
    const modal = modalStore.find((m) => m.content === PopupType.EDIT_PORTAL_LINK);
    if (!modal || !modal.payload) {
      return <div></div>;
    }
    const payload = modal.payload as EditPortalLinkPayload;
    return <EditPortalLinkPopup content={payload} />;
  } else if (modalContent === PopupType.DIALOG) {
    const modal = modalStore.find((m) => m.content === PopupType.DIALOG);
    if (!modal || !modal.payload) {
      console.warn('Empty popup payload, for dialog popups payload should be defined');
      return <div></div>;
    } else {
      const payload = modal.payload as DialogPopupPayload;
      return <DialogPopup content={payload} usePadding={modal.usePadding} />;
    }
  } else if (modalContent === PopupType.FILE_INFO) {
    const modal = modalStore.find((m) => m.content === PopupType.FILE_INFO);
    if (modal && modal.payload) {
      const payload = modal.payload as FileInfoPayload;
      if (payload.fileId) {
        return (
          <FileInfoPopup
            fileId={payload.fileId}
            orgRole={payload.orgRole}
            userId={payload.userId}
            fileOpenHost={payload.fileOpenHost}
            themeColors={payload.themeColors}
            themeStyle={payload.themeStyle}
            openFileLocationEnabled={payload.openFileLocationEnabled}
            canEditOtherUsersFile={payload.canEditOtherUsersFile}
            renameDisabled={payload.renameDisabled}
            deleteDisabled={payload.deleteDisabled}
          />
        );
      }
    }
    return <div></div>;
  } else if (modalContent === PopupType.SUCCESS_MOVE_COPY) {
    return <SuccessMoveCopyPopup />;
  } else if (modalContent === PopupType.MANAGE_MEMBERS) {
    const modal = modalStore.find((m) => m.content === PopupType.MANAGE_MEMBERS);
    if (modal && modal.payload) {
      const payload = modal.payload as ManageMembersPayload;
      return <ManageMembers {...payload.data} />;
    }
    return <div></div>;
  } else if (modalContent === PopupType.PUBLISH_INFORM) {
    return <PublishInformPopup />;
  } else if (modalContent === PopupType.TRANSCRIPT_LIMIT_LTD) {
    return <TranscriptLimitLTDPopup />;
  } else if (modalContent === PopupType.AUTOMATION_LIMIT_LTD) {
    return <AutomationLimitLTDPopup />;
  } else if (modalContent === PopupType.GET_TRANSCRIBE) {
    return <GetTranscribePopup />;
  } else if (modalContent === PopupType.PURCHASE_TRANSCRIBE_BILLING) {
    return <PurchaseTranscribeBillingPopup />;
  } else if (modalContent === PopupType.WIZARD_EXIT) {
    return <WizardExitPopup />;
  } else if (modalContent === PopupType.WIZARD_EXIT_THANKS) {
    return <WizardExitThanksPopup />;
  } else if (modalContent === PopupType.GET_AUTOMATION) {
    return <GetAutomationPopup />;
  } else if (modalContent === PopupType.PURCHASE_AUTOMATION_BILLING) {
    return <PurchaseAutomationBillingPopup />;
  } else if (modalContent === PopupType.ADDON) {
    return <AddonPopup />;
  } else if (modalContent === PopupType.TRANSCRIPT_AD) {
    const payload = modal?.payload as TranscriptADPopupPayload;
    return <TranscriptADPopup onClose={payload?.onClose} onTranscriptRequest={payload?.onTranscriptRequest} />;
  } else if (modalContent === PopupType.CREATE_PORTAL_ENTITY) {
    return <CreatePortalEntityPopup />;
  } else if (modalContent === PopupType.CREATE_PORTAL_LINK) {
    return <CreatePortalLinkPopup />;
  } else if (modalContent === PopupType.PROCESS_VIEW_INTRO_POPUP) {
    return <ProcessViewIntroPopup />;
  } else if (modalContent === PopupType.PORTAL_IS_READY) {
    return <PortalIsReadyPopup />;
  } else if (modalContent === PopupType.DELETE_PROJECTION_ORIGIN) {
    return <DeleteProjectionOriginPopup />;
  } else if (modalContent === PopupType.CUSTOMIZER_UNPUBLISHED_CHANGES) {
    const modal = modalStore.find((m) => m.content === PopupType.CUSTOMIZER_UNPUBLISHED_CHANGES);
    if (!modal || !modal.payload) {
      console.warn('Empty popup payload, for dialog popups payload should be defined');
      return <div></div>;
    } else {
      const payload = modal.payload as CustomizerUnpublishedChangesPopupPayload;
      return <CustomizerUnpublishedChangesPopup content={payload} usePadding={modal.usePadding} />;
    }
  }
  return null;
}
